/**
 * Default weights — per-CLIENT-org fallback shipping weight (kg) by product
 * type code (`org_default_weights.default_weights`), set by the 3PL OWNER on
 * the client's behalf.
 *
 * Shipping weight precedence (BLU-1272, D-03/D-04; `resolveShippingWeightKg`):
 *   1. the 3PL's shipping weight override for that client product at the
 *      shipping location (`sku_shipping_weight_overrides`), which WINS over
 *   2. the weight set on the product in Shopify (`skus.weight_value` /
 *      `weight_unit`), which wins over
 *   3. the client's default for the product type (this map).
 * The default only fills in a line nothing else covered, so a carrier that
 * requires a per-item weight (YunExpress) never has to reject an order over
 * catalog data the client never filled in.
 *
 * Map shape: `productTypeCode -> kg`, flat. Codes reuse `RIP_CODE_REGEX` and are
 * stored uppercased, matching the RIP routing / print-size-rules key posture.
 */
import { RIP_CODE_REGEX } from './rip-routing.js';
/** Inclusive kg bounds — matches the write-path zod; the DB only checks it is a JSON object. */
export const DEFAULT_WEIGHT_MIN_KG = 0.001;
export const DEFAULT_WEIGHT_MAX_KG = 30;
/** Bound the doc so a malformed/hostile payload can't store an unbounded map —
 *  mirrors the write-path zod; re-enforced by the sanitizer so a direct
 *  DB/PostgREST write bypassing that zod can't defeat it. */
export const DEFAULT_WEIGHTS_MAX_CODES = 64;
/** Round kg to the stored precision (3 dp, whole grams). */
export function roundKg(value) {
    return Math.round(value * 1000) / 1000;
}
function isValidKg(value) {
    return (typeof value === 'number' &&
        Number.isFinite(value) &&
        value >= DEFAULT_WEIGHT_MIN_KG &&
        value <= DEFAULT_WEIGHT_MAX_KG);
}
/**
 * Sanitize an untrusted/raw default-weights doc into a safe SUBSET: keys
 * trimmed + uppercased and checked against `RIP_CODE_REGEX`; values must be
 * finite numbers within `[DEFAULT_WEIGHT_MIN_KG, DEFAULT_WEIGHT_MAX_KG]`,
 * rounded to 3 decimals. Invalid entries are dropped silently (a single bad
 * entry never blanks the whole map). Capped at `DEFAULT_WEIGHTS_MAX_CODES`,
 * deterministically: the first N keys in sorted order are kept. Returns a FRESH
 * object (safe to mutate). Never throws.
 */
export function sanitizeDefaultWeights(raw) {
    const out = {};
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return out;
    const valid = new Map();
    for (const [key, value] of Object.entries(raw)) {
        const code = key.trim().toUpperCase();
        if (!RIP_CODE_REGEX.test(code))
            continue;
        if (!isValidKg(value))
            continue;
        if (!valid.has(code))
            valid.set(code, roundKg(value));
    }
    const codes = Array.from(valid.keys()).sort().slice(0, DEFAULT_WEIGHTS_MAX_CODES);
    for (const code of codes)
        out[code] = valid.get(code);
    return out;
}
const KG_PER_UNIT = new Map([
    ['g', 0.001],
    ['grams', 0.001],
    ['kg', 1],
    ['kilograms', 1],
    ['oz', 0.028349523125],
    ['ounces', 0.028349523125],
    ['lb', 0.45359237],
    ['lbs', 0.45359237],
    ['pounds', 0.45359237],
]);
/**
 * Convert a stored catalog weight to kilograms (3 dp). Accepts Shopify's
 * `GRAMS | KILOGRAMS | OUNCES | POUNDS` plus short forms, case-insensitively.
 * Returns 0 for an unknown unit, a non-finite value, or a result that is not
 * positive — 0 always means "no usable weight", so callers can `||` fall
 * through to the next source. Never throws.
 */
export function weightToKg(value, unit) {
    const amount = typeof value === 'number'
        ? value
        : typeof value === 'string' && value.trim() !== ''
            ? Number(value)
            : NaN;
    if (!Number.isFinite(amount) || amount <= 0)
        return 0;
    if (typeof unit !== 'string')
        return 0;
    const factor = KG_PER_UNIT.get(unit.trim().toLowerCase());
    if (factor === undefined)
        return 0;
    const kg = roundKg(amount * factor);
    return kg > 0 ? kg : 0;
}
const SKU_CODE_PREFIXES = ['CUST-', 'BLANK-', 'PW-'];
/**
 * Product-type code from a SKU: the first hyphen segment, after stripping
 * leading `CUST-` / `BLANK-` / `PW-` markers. `TEE-L-BK` -> `TEE`,
 * `CUST-CBP-BL-ABC` -> `CBP`, `BLANK-HT-XS-BK` -> `HT`. A SKU with no hyphen
 * left after stripping, or whose first segment is not a valid code, yields
 * `null`.
 */
export function productTypeCodeFromSku(sku) {
    if (typeof sku !== 'string')
        return null;
    let rest = sku.trim().toUpperCase();
    for (let pass = 0; pass < 3; pass += 1) {
        const prefix = SKU_CODE_PREFIXES.find((p) => rest.startsWith(p));
        if (!prefix)
            break;
        rest = rest.slice(prefix.length);
    }
    if (!rest.includes('-'))
        return null;
    const code = rest.split('-')[0];
    return RIP_CODE_REGEX.test(code) ? code : null;
}
/** Type code for an order line: the base `sku` first, then `shopify_sku`. */
export function productTypeCodeForLine(line) {
    return productTypeCodeFromSku(line.sku) ?? productTypeCodeFromSku(line.shopify_sku);
}
/** Dual-print prefix (`2HT` -> `HT`). Never `2PACK`: that is a multipack, not a dual print. */
const DUAL_PRINT_CODE_REGEX = /^2(?!PACK$)[A-Z][A-Z0-9]*$/;
function getOwnKg(map, code) {
    if (typeof map !== 'object' || map === null || Array.isArray(map))
        return null;
    if (!Object.prototype.hasOwnProperty.call(map, code))
        return null;
    const value = map[code];
    return isValidKg(value) ? value : null;
}
/**
 * Default kg for a product-type code: an exact (uppercase) match first, then
 * for a dual-print code (`2HT`) its single-print entry (`HT`) when there is no
 * exact entry. `2PACK` / `3PACK` are never rewritten. Returns `null` when
 * nothing applies. Never throws.
 */
export function resolveDefaultWeightKg(map, code) {
    if (typeof code !== 'string' || code.length === 0)
        return null;
    const upper = code.trim().toUpperCase();
    const exact = getOwnKg(map, upper);
    if (exact !== null)
        return exact;
    if (DUAL_PRINT_CODE_REGEX.test(upper))
        return getOwnKg(map, upper.slice(1));
    return null;
}
/**
 * BLU-1272: the shipping weight chain, shared by the YunExpress shipment
 * builder and the client-catalog badge (D-03, D-04):
 *   1. the first `overrideKg` entry that is finite and within the kg bounds,
 *      in array order (callers pass base row, then design row);
 *   2. otherwise the first `catalogRows` entry whose Shopify weight converts to
 *      a positive kg (each row with its OWN unit, so a row with an unusable
 *      unit falls through to the next);
 *   3. otherwise a valid `defaultKg`;
 *   4. otherwise `{ kg: 0, source: null }`.
 * Any override beats any Shopify weight. Pure; never throws.
 *
 * Not the only place step 3 runs: the shipment builder
 * (`getYunExpressShipment`, apps/backend shipping.dao.ts) passes
 * `defaultKg: null` and applies the default itself, so it reads the client's
 * default weights only when steps 1 and 2 left a line without a weight. A
 * change to the order here must be made there too, or the label and the
 * badge disagree.
 */
export function resolveShippingWeightKg(input) {
    for (const value of input.overrideKg) {
        if (isValidKg(value))
            return { kg: roundKg(value), source: 'override' };
    }
    for (const row of input.catalogRows) {
        const kg = weightToKg(row?.weight_value, row?.weight_unit);
        if (kg > 0)
            return { kg, source: 'shopify' };
    }
    if (isValidKg(input.defaultKg))
        return { kg: roundKg(input.defaultKg), source: 'default' };
    return { kg: 0, source: null };
}
